import { sortBy } from 'lodash'
import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { replaceAndStrip } from '../../../utils/string'
import {
  updateCurrentTimelineSelectContract,
  updateCurrentTimelineSelectOccupation,
  updateCurrentTimelineSelectPlace,
  updateCurrentTimelineSelectTeam,
} from '../../../states/containers/common/currentSlice'
import { fetchOccupation } from '../../../states/containers/member/memberSlice'
import { compile } from '../../../utils/search'

interface Props {
  isDisplayFilter: boolean
  setIsDisplayFilter: (value: boolean) => void
}

interface DisplayTeam {
  id: number
  name: string
  color: string
  checked: boolean
  display: boolean
}

interface DisplayOccupation {
  id: number
  name: string
  checked: boolean
  display: boolean
}

interface DisplayPlaces {
  name: string
  checked: boolean
  display: boolean
}

interface DisplayContracts {
  name: string
  checked: boolean
  display: boolean
}

type DisplayList = DisplayTeam | DisplayOccupation | DisplayPlaces | DisplayContracts

const Search: FC<Props> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const {
    members,
    teams,
    team_manager_ids,
  } = useSelector((state: RootState) => state.commonPart)

  const {
    current,
    member,
  } = useSelector((state: RootState) => state)

  const { isDisplayFilter, setIsDisplayFilter } = props

  const [isDisplaySelectBox, setIsDisplaySelectBox] = useState<boolean>(false)
  const [isDisplayTeamSelectBox, setIsDisplayTeamSelectBox] = useState<boolean>(false)
  const [isDisplayOccupationSelectBox, setIsDisplayOccupationSelectBox] = useState<boolean>(false)
  const [isDisplayContractSelectBox, setIsDisplayContractSelectBox] = useState<boolean>(false)
  const [isDisplayPlaceSelectBox, setIsDisplayPlaceSelectBox] = useState<boolean>(false)

  const [teamList, setTeamList] = useState<DisplayTeam[]>()
  const [occupationList, setOccupationList] = useState<DisplayOccupation[]>()
  const [contractList, setContractList] = useState<DisplayContracts[]>()
  const [placeList, setPlaceList] = useState<DisplayPlaces[]>()

  const [searchTeamsWord, setSearchTeamsWord] = useState<string>("")
  const [searchOccupationsWord, setSearchOccupationsWord] = useState<string>("")
  const [searchContractsWord, setSearchContractsWord] = useState<string>("")
  const [searchPlacesWord, setSearchPlacesWord] = useState<string>("")

  const isCurrentFiltered = (paramName: string) => {
    const searchParam = new URLSearchParams(location.search)
    const targetParam = searchParam.get(paramName)
    if (targetParam) {
      if (paramName === 'teams' || paramName === 'occupations') {
        return targetParam?.split(',').map((value) => Number(value)) || []
      }
      return targetParam?.split(',') || []
    }
    if (paramName === 'teams') {
      return current.timelineSelectTeam?.split(',').map((team) => Number(team)) || []
    }
    if (paramName === 'occupations') {
      return current.timelineSelectOccupation?.split(',').map((occupation) => Number(occupation)) || []
    }
    if (paramName === 'contracts') {
      return current.timelineSelectContract?.split(',') || []
    }
    if (paramName === 'places') {
      return current.timelineSelectPlace?.split(',') || []
    }
    return []
  }

  const initTeamList = () => {
    const selectableTeams = teams?.filter((team) => team_manager_ids?.includes(team.id))
    setTeamList(
      selectableTeams?.map(
        (team) => (
          (isCurrentFiltered('teams') as number[]).includes(team.id)
            ? { ...team, checked: true, display: true }
            : { ...team, checked: false, display: true }
        )
      ),
    )
  }

  const initOccupationList = () => {
    setOccupationList(
      member.occupationList?.map(
        (occupation) => (
          (isCurrentFiltered('occupations') as number[]).includes(occupation.id)
            ? { ...occupation, checked: true, display: true }
            : { ...occupation, checked: false, display: true }
        )
      ),
    )
  }

  const initContractList = () => {
    if (!members) return
    const contracts = Array.from(new Set(members?.map((value) => value.contract))).filter(Boolean) as string[]
    setContractList(
      contracts?.map(
        (contract) => (
          (isCurrentFiltered('contracts') as string[]).includes(contract)
            ? { name: contract, checked: true, display: true }
            : { name: contract, checked: false, display: true }
        )
      ),
    )
  }

  const initPlaceList = () => {
    if (!members) return
    const places = Array.from(new Set(members?.map((value) => value.place))).filter(Boolean) as string[]
    setPlaceList(
      places?.map(
        (place) => (
          (isCurrentFiltered('places') as string[]).includes(place)
            ? { name: place, checked: true, display: true }
            : { name: place, checked: false, display: true }
        )
      ),
    )
  }

  useEffect(() => {
    if (isDisplayFilter) return
    initTeamList()
    initOccupationList()
    initContractList()
    initPlaceList()
  }, [isDisplayFilter])

  const setFilter = () => {
    const selectedTeam = teamList?.filter((team) => team.checked === true).map((team) => team.id).join(',') || ""
    dispatch(updateCurrentTimelineSelectTeam(selectedTeam))
    const selectedOccupation = occupationList?.filter((occupation) => occupation.checked === true).map((occupation) => occupation.id).join(',') || ""
    dispatch(updateCurrentTimelineSelectOccupation(selectedOccupation))
    const selectedContract = contractList?.filter((contract) => contract.checked === true).map((contract) => contract.name).join(',') || ""
    dispatch(updateCurrentTimelineSelectContract(selectedContract))
    const selectedPlace = placeList?.filter((place) => place.checked === true).map((place) => place.name).join(',') || ""
    dispatch(updateCurrentTimelineSelectPlace(selectedPlace))
    setIsDisplayFilter(!isDisplayFilter)
  }

  // team
  const clickTeams = (teamId: number) => {
    if (!teamId) return
    if (teamList?.find((team) => team.id === teamId)?.checked) {
      setTeamList(teamList?.map((team) => (team.id === teamId ? { ...team, checked: false } : team)))
    } else {
      setTeamList(teamList?.map((team) => (team.id === teamId ? { ...team, checked: true } : team)))
    }
  }

  const searchTeams = (words: string) => {
    setSearchTeamsWord(words)
    if (!words) {
      setTeamList(teamList?.map((team) => ({ ...team, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = teamList?.map((team) => {
      if (compile(word).test(team.name)) {
        return { ...team, display: true }
      }
      return { ...team, display: false }
    })
    setTeamList(sortBy(list, 'id'))
  }

  const resetSearchTeams = () => {
    setSearchTeamsWord("")
    setTeamList(teamList?.map((team) => ({ ...team, display: true })))
  }

  const resetTeams = () => {
    setTeamList(
      teamList?.map((team) => ({ ...team, checked: false })),
    )
  }

  // occupation
  const clickOccupations = (occupationId: number) => {
    if (!occupationId) return
    if (occupationList?.find((occupation) => occupation.id === occupationId)?.checked) {
      setOccupationList(occupationList?.map((occupation) => (occupation.id === occupationId ? { ...occupation, checked: false } : occupation)))
    } else {
      setOccupationList(occupationList?.map((occupation) => (occupation.id === occupationId ? { ...occupation, checked: true } : occupation)))
    }
  }

  const searchOccupations = (words: string) => {
    setSearchOccupationsWord(words)
    if (!words) {
      setOccupationList(occupationList?.map((occupation) => ({ ...occupation, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = occupationList?.map((occupation) => {
      if (compile(word).test(t(occupation.name))) {
        return { ...occupation, display: true }
      }
      return { ...occupation, display: false }
    })
    setOccupationList(sortBy(list, 'id'))
  }

  const resetSearchOccupations = () => {
    setSearchOccupationsWord("")
    setOccupationList(occupationList?.map((occupation) => ({ ...occupation, display: true })))
  }

  const resetOccupations = () => {
    setOccupationList(
      occupationList?.map((occupation) => ({ ...occupation, checked: false })),
    )
  }

  // contract
  const clickContracts = (contractName: string) => {
    if (!contractName) return
    if (contractList?.find((contract) => contract.name === contractName)?.checked) {
      setContractList(contractList?.map((contract) => (contract.name === contractName ? { ...contract, checked: false } : contract)))
    } else {
      setContractList(contractList?.map((contract) => (contract.name === contractName ? { ...contract, checked: true } : contract)))
    }
  }

  const searchContracts = (words: string) => {
    setSearchContractsWord(words)
    if (!words) {
      setContractList(contractList?.map((contract) => ({ ...contract, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = contractList?.map((contract) => {
      if (compile(word).test(contract.name)) {
        return { ...contract, display: true }
      }
      return { ...contract, display: false }
    })
    setContractList(list)
  }

  const resetSearchContracts = () => {
    setSearchContractsWord("")
    setContractList(contractList?.map((contract) => ({ ...contract, display: true })))
  }

  const resetContracts = () => {
    setContractList(
      contractList?.map((contract) => ({ ...contract, checked: false })),
    )
  }

  // place
  const clickPlaces = (placeName: string) => {
    if (!placeName) return
    if (placeList?.find((place) => place.name === placeName)?.checked) {
      setPlaceList(placeList?.map((place) => (place.name === placeName ? { ...place, checked: false } : place)))
    } else {
      setPlaceList(placeList?.map((place) => (place.name === placeName ? { ...place, checked: true } : place)))
    }
  }

  const searchPlaces = (words: string) => {
    setSearchPlacesWord(words)
    if (!words) {
      setPlaceList(placeList?.map((place) => ({ ...place, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = placeList?.map((place) => {
      if (compile(word).test(place.name)) {
        return { ...place, display: true }
      }
      return { ...place, display: false }
    })
    setPlaceList(list)
  }

  const resetSearchPlaces = () => {
    setSearchPlacesWord("")
    setPlaceList(placeList?.map((place) => ({ ...place, display: true })))
  }

  const resetPlaces = () => {
    setPlaceList(
      placeList?.map((place) => ({ ...place, checked: false })),
    )
  }

  // chips
  const rejectChips = (value: DisplayList | undefined, type: string) => {
    if (!value) return
    if (type === 'team' && 'id' in value) {
      clickTeams(value.id)
    } else if (type === 'occupation' && 'id' in value) {
      clickOccupations(value.id)
    } else if (type === 'contract' && 'name' in value) {
      clickContracts(value.name)
    } else if (type === 'place' && 'name' in value) {
      clickPlaces(value.name)
    }
  }

  const buildChips = (list: DisplayList[] | undefined, type: string, deletable = false) => {
    if (!list) return ""
    return list.map((value) => (
      <div className="chip" key={value.name}>
        {t(value.name)}
        {
          deletable && (
            <FontAwesomeIcon
              icon={['fas', 'times']}
              className="icon"
              onClick={() => rejectChips(value, type)}
            />
          )
        }
      </div>
    ))
  }

  const openTeamSelectBox = () => {
    setIsDisplayTeamSelectBox(true)
    setIsDisplayOccupationSelectBox(false)
    setIsDisplayContractSelectBox(false)
    setIsDisplayPlaceSelectBox(false)
    setIsDisplaySelectBox(true)
  }

  const openOccupationSelectBox = () => {
    setIsDisplayTeamSelectBox(false)
    setIsDisplayOccupationSelectBox(true)
    setIsDisplayContractSelectBox(false)
    setIsDisplayPlaceSelectBox(false)
    setIsDisplaySelectBox(true)
  }

  const openContractSelectBox = () => {
    setIsDisplayTeamSelectBox(false)
    setIsDisplayOccupationSelectBox(false)
    setIsDisplayContractSelectBox(true)
    setIsDisplayPlaceSelectBox(false)
    setIsDisplaySelectBox(true)
  }

  const openPlaceSelectBox = () => {
    setIsDisplayTeamSelectBox(false)
    setIsDisplayOccupationSelectBox(false)
    setIsDisplayContractSelectBox(false)
    setIsDisplayPlaceSelectBox(true)
    setIsDisplaySelectBox(true)
  }

  const closeSelectBox = () => {
    setIsDisplayTeamSelectBox(false)
    setIsDisplayOccupationSelectBox(false)
    setIsDisplayContractSelectBox(false)
    setIsDisplayPlaceSelectBox(false)
    setIsDisplaySelectBox(false)
    resetSearchTeams()
    resetSearchOccupations()
    resetSearchContracts()
    resetSearchPlaces()
  }

  useEffect(() => {
    initTeamList()
  }, [teams])

  useEffect(() => {
    initOccupationList()
  }, [member.occupationList])

  useEffect(() => {
    initContractList()
    initPlaceList()
  }, [members])

  useEffect(() => {
    dispatch(fetchOccupation())
  }, [])

  return (
    <section className="dashboard-search-section">
      <div className="header_area">
        {
          !isDisplayFilter && (
            <div className="search_conditions">
              {
                current.timelineSelectTeam.length !== 0 && (
                  <>
                    <div className="title">
                      {t('dashboard.search.team.title')}
                    </div>
                    {buildChips(
                      teamList?.filter((team) => current.timelineSelectTeam.split(',').includes(String(team.id))),
                      'team',
                    )}
                  </>
                )
              }
              {
                current.timelineSelectOccupation.length !== 0 && (
                  <>
                    <div className="title">
                      {t('dashboard.search.occupation.title')}
                    </div>
                    {buildChips(
                      occupationList?.filter((occupation) => current.timelineSelectOccupation.split(',').includes(String(occupation.id))),
                      'occupation',
                    )}
                  </>
                )
              }
              {
                current.timelineSelectContract.length !== 0 && (
                  <>
                    <div className="title">
                      {t('dashboard.search.contract.title')}
                    </div>
                    {buildChips(
                      contractList?.filter((contract) => current.timelineSelectContract.split(',').includes(contract.name)),
                      'contract',
                    )}
                  </>
                )
              }
              {
                current.timelineSelectPlace.length !== 0 && (
                  <>
                    <div className="title">
                      {t('dashboard.search.place.title')}
                    </div>
                    {buildChips(
                      placeList?.filter((place) => current.timelineSelectPlace.split(',').includes(place.name)),
                      'place',
                    )}
                  </>
                )
              }
            </div>
          )
        }
      </div>
      <div className={`contents_area ${isDisplayFilter && "open"}`}>
        <div className="conditions_area">
          <div className="conditions_item">
            <div className="conditions_title">
              {t('dashboard.search.team.title')}
              {
                teamList?.filter((team) => team.checked === true).length !== 0 && (
                  <div
                    className="reset"
                    role="none"
                    onClick={resetTeams}
                  >
                    {t('dashboard.search.reset')}
                  </div>
                )
              }
            </div>
            <div className="selected_item">
              <div
                className="modal_trigger"
                role="none"
                onClick={openTeamSelectBox}
              />
              {buildChips(
                teamList?.filter((team) => team.checked),
                'team',
                true,
              )}
              <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isDisplayTeamSelectBox && 'open'}`} />
            </div>
            <div className={`select_box ${isDisplayTeamSelectBox || "none"}`}>
              <div className="search_area">
                <FontAwesomeIcon
                  icon={['fas', 'search']}
                  className="icon"
                />
                <input
                  type="text"
                  placeholder={t('dashboard.search.team.placeholder')}
                  onChange={(event) => searchTeams(event.target.value)}
                  value={searchTeamsWord}
                />
                {
                  searchTeamsWord && (
                    <button type="button" className="reset" onClick={resetSearchTeams}>
                      <FontAwesomeIcon icon={['far', 'times-circle']} />
                    </button>
                  )
                }
              </div>
              <div className="select_list">
                {
                  teamList && teamList.map((team: DisplayTeam) => (
                    team.display && (
                      <div
                        key={team.id}
                        className="select_row"
                      >
                        <input
                          type="checkbox"
                          id={`filter_team_${team.name}`}
                          name="teams"
                          value={team.id}
                          checked={team.checked}
                          onChange={() => clickTeams(team.id)}
                        />
                        <label htmlFor={`filter_team_${team.name}`}>
                          {team.name}
                        </label>
                      </div>
                    )
                  ))
                }
              </div>
            </div>
          </div>
          <div className="conditions_item">
            <div className="conditions_title">
              {t('dashboard.search.occupation.title')}
              {
                occupationList?.filter((contract) => contract.checked === true).length !== 0 && (
                  <div
                    className="reset"
                    role="none"
                    onClick={resetOccupations}
                  >
                    {t('dashboard.search.reset')}
                  </div>
                )
              }
            </div>
            <div className="selected_item">
              <div
                className="modal_trigger"
                role="none"
                onClick={openOccupationSelectBox}
              />
              {buildChips(
                occupationList?.filter((occupation) => occupation.checked),
                'occupation',
                true,
              )}
              <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isDisplayOccupationSelectBox && 'open'}`} />
            </div>
            <div className={`select_box ${isDisplayOccupationSelectBox || "none"}`}>
              <div className="search_area">
                <FontAwesomeIcon
                  icon={['fas', 'search']}
                  className="icon"
                />
                <input
                  type="text"
                  placeholder={t('dashboard.search.occupation.placeholder')}
                  onChange={(event) => searchOccupations(event.target.value)}
                  value={searchOccupationsWord}
                />
                {
                  searchOccupationsWord && (
                    <button type="button" className="reset" onClick={resetSearchOccupations}>
                      <FontAwesomeIcon icon={['far', 'times-circle']} />
                    </button>
                  )
                }
              </div>
              <div className="select_list">
                {
                  occupationList && occupationList.map((occupation: DisplayOccupation) => (
                    occupation.display && (
                      <div
                        key={occupation.id}
                        className="select_row"
                      >
                        <input
                          type="checkbox"
                          id={`filter_occupation_${occupation.name}`}
                          name="occupations"
                          value={occupation.id}
                          checked={occupation.checked}
                          onChange={() => clickOccupations(occupation.id)}
                        />
                        <label htmlFor={`filter_occupation_${occupation.name}`}>
                          {t(`${occupation.name}`)}
                        </label>
                      </div>
                    )
                  ))
                }
              </div>
            </div>
          </div>
          <div className="conditions_item">
            <div className="conditions_title">
              {t('dashboard.search.contract.title')}
              {
                contractList?.filter((contract) => contract.checked === true).length !== 0 && (
                  <div
                    className="reset"
                    role="none"
                    onClick={resetContracts}
                  >
                    {t('dashboard.search.reset')}
                  </div>
                )
              }
            </div>
            <div className="selected_item">
              <div
                className="modal_trigger"
                role="none"
                onClick={openContractSelectBox}
              />
              {buildChips(
                contractList?.filter((contract) => contract.checked),
                'contract',
                true,
              )}
              <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isDisplayContractSelectBox && 'open'}`} />
            </div>
            <div className={`select_box ${isDisplayContractSelectBox || "none"}`}>
              <div className="search_area">
                <FontAwesomeIcon
                  icon={['fas', 'search']}
                  className="icon"
                />
                <input
                  type="text"
                  placeholder={t('dashboard.search.contract.placeholder')}
                  onChange={(event) => searchContracts(event.target.value)}
                  value={searchContractsWord}
                />
                {
                  searchContractsWord && (
                    <button type="button" className="reset" onClick={resetSearchContracts}>
                      <FontAwesomeIcon icon={['far', 'times-circle']} />
                    </button>
                  )
                }
              </div>
              <div className="select_list">
                {
                  contractList && contractList.map((contract: DisplayContracts) => (
                    contract.display && (
                      <div
                        key={contract.name}
                        className="select_row"
                      >
                        <input
                          type="checkbox"
                          id={`filter_contract_${contract.name}`}
                          name="contracts"
                          value={contract.name}
                          checked={contract.checked}
                          onChange={() => clickContracts(contract.name)}
                        />
                        <label htmlFor={`filter_contract_${contract.name}`}>
                          {contract.name}
                        </label>
                      </div>
                    )
                  ))
                }
              </div>
            </div>
          </div>
          <div className="conditions_item">
            <div className="conditions_title">
              {t('dashboard.search.place.title')}
              {
                placeList?.filter((place) => place.checked === true).length !== 0 && (
                  <div
                    className="reset"
                    role="none"
                    onClick={resetPlaces}
                  >
                    {t('dashboard.search.reset')}
                  </div>
                )
              }
            </div>
            <div className="selected_item">
              <div
                className="modal_trigger"
                role="none"
                onClick={openPlaceSelectBox}
              />
              {buildChips(
                placeList?.filter((place) => place.checked),
                'place',
                true,
              )}
              <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isDisplayPlaceSelectBox && 'open'}`} />
            </div>
            <div className={`select_box ${isDisplayPlaceSelectBox || "none"}`}>
              <div className="search_area">
                <FontAwesomeIcon
                  icon={['fas', 'search']}
                  className="icon"
                />
                <input
                  type="text"
                  placeholder={t('dashboard.search.place.placeholder')}
                  onChange={(event) => searchPlaces(event.target.value)}
                  value={searchPlacesWord}
                />
                {
                  searchPlacesWord && (
                    <button type="button" className="reset" onClick={resetSearchPlaces}>
                      <FontAwesomeIcon icon={['far', 'times-circle']} />
                    </button>
                  )
                }
              </div>
              <div className="select_list">
                {
                  placeList && placeList.map((place: DisplayPlaces) => (
                    place.display && (
                      <div
                        key={place.name}
                        className="select_row"
                      >
                        <input
                          type="checkbox"
                          id={`filter_place_${place.name}`}
                          name="places"
                          value={place.name}
                          checked={place.checked}
                          onChange={() => clickPlaces(place.name)}
                        />
                        <label htmlFor={`filter_place_${place.name}`}>
                          {place.name}
                        </label>
                      </div>
                    )
                  ))
                }
              </div>
            </div>
          </div>
        </div>
        <div className="button_area">
          <button
            type="button"
            className="cancel"
            onClick={() => setIsDisplayFilter(!isDisplayFilter)}
          >
            {t('dashboard.search.cancel')}
          </button>
          <button
            type="button"
            className="submit"
            onClick={setFilter}
          >
            {t('dashboard.search.submit')}
          </button>
        </div>
      </div>
      <div className={`dashboard_screen ${isDisplaySelectBox || 'none'}`} role="presentation" onClick={closeSelectBox} />
    </section>
  )
}

export default Search
